import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import CommonTable, { ICommonTable } from 'src/components/common/CommonTable';
import { IDepartment, IDoctorInfo, ISales } from 'src/entity/DoctorEntity';
import { IDoctorSearch, IPropsLocationState } from 'src/entity/SearchEntity';
import I18N from 'src/services/I18NService';
import { Dispatch } from 'redux';
import { message, Row, Button, Icon, Modal } from 'antd';
import DoctorService from 'src/services/DoctorService';
import { fetchDrugStoresIfNeeded } from 'src/redux/actions/drugStoreCreator';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import { fetchAllPharmacyList } from 'src/redux/actions/mallPharmacyCreator';
import { IMallPharmacyDetail } from 'src/entity/MallEntity';
import { RouterConfig } from 'src/routers/urlConfig';
import DoctorSearch from './DoctorSearch';
import SupplierEdit from 'src/containers/SupplierEdit';
import VASOrderAdd from 'src/containers/VASOrderAdd';
import { ColumnProps } from 'antd/lib/table';
import { isArray } from 'util';
import { UtilDateTime } from 'src/utils/datetime';
import { selectDoctor } from 'src/redux/actions/selectDoctorCreator';
import { DOCTOR_TYPE_DETAIL, DOCTOR_TYPE_LIST } from 'src/constants/ActionTypes';
import FileService from 'src/services/FileService';
import { fetchMallPharmacyIfNeeded } from '../../../redux/actions/mallPharmacyCreator';
import AssignSales from 'src/components/common/AssignSales';
import { getLocationNameChain } from 'src/redux/actions/locationCreator';

interface IDoctorListProps extends RouteComponentProps<{}, {}, IPropsLocationState> {
  isAgent: boolean;
  isDomestic: boolean;
  userInfo: IAgentInfo | IDrugStoreInfo | undefined;
  drugStores: IDrugStoreInfo[];
  dispatch: Dispatch<any>;
}

interface IDoctorListState extends ICommonTable<IDoctorInfo> {
  /** 查询条件 */
  query: IDoctorSearch;
  /** 是否是待配置医生, 默认 false： 医生列表  true： 待配置列表 */
  isNeedSetting: boolean;
  /** 代理商(以及关联的配药中心)生效的单元药库 */
  allPharmacyList: IMallPharmacyDetail[];
  /** 编辑供应商的窗口显示状态 */
  visibleSupplier: boolean;
  /** 编辑订单窗口显示状态 */
  visibleOrder: boolean;
  /** loading状态 */
  isLoading: boolean;
  visibleDocAssign: boolean;
  selectedRowKeys: number[];
  docInfos: IDoctorInfo[];
}

class DoctorList extends React.Component<IDoctorListProps, IDoctorListState> {
  tableComponent: CommonTable | null;

  constructor(props: IDoctorListProps) {
    super(props);
    // 根据url判断菜单
    let isNeedSetting = false;
    if (props.match.url === RouterConfig.Urls.userDoctor) {
      isNeedSetting = false;
    } else if (props.match.url === RouterConfig.Urls.userDoctorNoSetting) {
      isNeedSetting = true;
    }

    const commonColumns: Array<ColumnProps<IDoctorInfo>> = [
      {
        title: I18N.get('common_rowNum'),
        key: 'index',
        dataIndex: 'index',
      },
      {
        title: I18N.get('common_name'),
        key: 'name',
        dataIndex: 'name',
        render: (text: string[], record: IDoctorInfo, index: number): React.ReactNode => {
          return `${record.last_name}${record.first_name}`;
        },
      },
      {
        title: this.props.isDomestic ? I18N.get('common_mobile') : 'email',
        key: 'phone',
        dataIndex: 'phone',
        render: (text: string[], record: IDoctorInfo, index: number): React.ReactNode => {
          return this.props.isDomestic ? record.mobile : record.email;
        },
      },
      {
        title: I18N.get('login_userName'),
        key: 'username',
        dataIndex: 'username',
      },
      {
        title: I18N.get('doc_position'),
        key: 'position',
        dataIndex: 'position',
      },
      {
        title: I18N.get('common_status'),
        key: 'status',
        dataIndex: 'status',
        render: (text: string[], record: IDoctorInfo, index: number): React.ReactNode => {
          return record.is_certificated ? I18N.get('common_status_authenticated') : I18N.get('common_status_unauthorized');
        },
      },
      {
        title: I18N.get('doc_area'),
        key: 'area',
        dataIndex: 'area',
        render: (text: string[], record: IDoctorInfo, index: number): React.ReactNode => {
          let area = getLocationNameChain(record.location_id).join(' ');
          area = area + (record.work_address !== '未知' ? record.work_address : '');
          return area;
        },
      },
      {
        title: I18N.get('doc_validity_date'),
        key: 'valid_time',
        dataIndex: 'valid_time',
      },
      {
        title: I18N.get('doc_sales'),
        key: 'salesperson_name',
        dataIndex: 'salesperson_name',
      },
      {
        title: I18N.get('common_create_time'),
        key: 'created_time',
        dataIndex: 'created_time',
        render: (text: string[], record: IDoctorInfo, index: number): React.ReactNode => {
          return UtilDateTime.datetimeFormat('yyyy-MM-dd', new Date(record.created_time));
        },
      },
      {
        title: I18N.get('doc_handling_time'),
        key: 'verification_time',
        dataIndex: 'verification_time',
        render: (text: string[], record: IDoctorInfo, index: number): React.ReactNode => {
          return UtilDateTime.datetimeFormat('yyyy-MM-dd', new Date(record.verification_time));
        },
      },
      {
        title: I18N.get('order_online'),
        key: 'online_ordering',
        dataIndex: 'online_ordering',
        render: (text: string[], record: IDoctorInfo, index: number): React.ReactNode => {
          return !!record.online_ordering ? I18N.get('common_yes') : I18N.get('common_no');
        },
      },
      {
        title: I18N.get('doc_enabled'),
        key: 'is_active',
        dataIndex: 'is_active',
        render: (text: string[], record: IDoctorInfo, index: number): React.ReactNode => {
          return record.is_active ? I18N.get('common_yes') : I18N.get('common_no');
        },
      },
      {
        title: I18N.get('common_operation'),
        dataIndex: 'operation',
        key: 'operation',
        render: (text: any, record: IDoctorInfo, index: number): React.ReactNode => {
          return (
            <div>
              {this.props.isAgent && this.props.isDomestic && (
                <a onClick={() => this.handleChangeSupplier(record, true)}>{`${I18N.get('common_edit')}${I18N.get('med_supplier')}`}</a>
              )}
              {this.props.isAgent && (
                <a onClick={() => this.handleAddOrder(record, true)}>&nbsp;&nbsp;{`${I18N.get('common_new')}${I18N.get('common_orders')}`}</a>
              )}
              {this.props.isAgent && !!record && !record.is_active && (
                <a onClick={() => this.handleDoctorHandle(record, true)}>&nbsp;&nbsp;{I18N.get('common_status_start')}</a>
              )}
              {this.props.isAgent && !!record && !!record.is_active && (
                <a onClick={() => this.handleDoctorHandle(record, false)}>&nbsp;&nbsp;{I18N.get('common_status_stop')}</a>
              )}
              <a onClick={() => this.handleDoctorDetail(record)}>&nbsp;&nbsp;{I18N.get('common_detail')}</a>
            </div>
          );
        },
      },
    ];

    if (!this.props.isDomestic) {
      const index = commonColumns.findIndex((t) => t.key === 'online_ordering');
      if (index > -1) {
        commonColumns.splice(index, 1);
      }
    }

    // 如果没有医生分配权限， 则不显示销售以及相关搜索
    const hasAssignAuth = (this.props.userInfo as IAgentInfo).customer_ownership;
    if (!hasAssignAuth) {
      const index = commonColumns.findIndex((t) => t.key === 'salesperson_name');
      if (index > -1) {
        commonColumns.splice(index, 1);
      }
    }

    this.state = {
      rowKey: 'id',
      loadingTable: false,
      columns: commonColumns,
      dataSource: [],
      // 医生的信息（姓名， 电话， 所属销售）
      docInfos: [],
      query: {
        check: true,
        offset: 0,
        limit: 10,
        is_verified: true,
        is_need_configure: isNeedSetting,
      },
      isNeedSetting,
      allPharmacyList: [],
      visibleSupplier: false,
      visibleOrder: false,
      visibleDocAssign: false,
      isLoading: false,
      // 选中的医生Id
      selectedRowKeys: [],
    } as IDoctorListState;
  }

  async componentDidMount() {
    // 获取配药中心数据
    await this.props.dispatch(fetchDrugStoresIfNeeded());
    // 获取代理商+所有配药中心的单元药库数据
    if (!!this.props.userInfo) {
      const sellerUserUUIDList = [this.props.userInfo.seller_user].concat(this.props.drugStores.map((t) => t.seller_user));
      const allPharmacyList = await fetchAllPharmacyList(sellerUserUUIDList);
      this.setState({
        allPharmacyList,
      });
    }
    // 查询
    if (!!this.props.location.state && !!this.props.location.state.query) {
      this.doSearch(this.props.location.state.query);
    } else {
      this.handleSearch();
    }
    // 获取单元药库数据
    this.props.dispatch(fetchMallPharmacyIfNeeded());
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  /**
   * 默认查询
   *
   * @memberof DoctorList
   */
  handleSearch = async (offset: number = 0, limit: number = 10) => {
    const query = Object.assign({}, this.state.query, {
      limit,
      offset,
    });
    await this.doSearch(query);
  };

  /**
   * 按条件查询
   *
   * @memberof DoctorList
   */
  doSearch = async (query: IDoctorSearch) => {
    if (this.state.loadingTable) {
      return;
    }
    try {
      this.setState({
        loadingTable: true,
        query,
      });
      const res = await DoctorService.getAgentDoctor(query);
      if (!!res) {
        this.setState({
          dataSource: res.results,
          total: res.count,
        });
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_search_failure'));
      }
    } finally {
      this.setState({
        loadingTable: false,
      });
    }
  };



  /**
   * 前往批量编辑供应商页面
   *
   * @memberof DoctorList
   */
  goToBatchModifyPage = () => {
    this.props.history.push(`${RouterConfig.Urls.userDoctorBatchModify}`);
  };

  /**
   * 医生导出
   *
   * @memberof DoctorList
   */
  doctorExport = async () => {
    if (this.state.isLoading) {
      return;
    }
    try {
      this.setState({
        isLoading: true,
      });
      const query = Object.assign(this.state.query, {
        limit: 100,
      });

      const res = await DoctorService.getAgentDoctorForExport(query);
      if (!!res) {
        FileService.doctorExport(res.results || []);
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_export_failure'));
      }
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  /**
   * 编辑供应商回调
   *
   * @memberof DoctorList
   */
  handleSupplierVisible = (value: boolean, doRefresh = false) => {
    this.setState({
      visibleSupplier: value,
    });
    if (doRefresh) {
      const { offset, limit } = this.state.query;
      this.handleSearch(offset, limit);
    }
  };

  /**
   * 选中项发生变化时的回调
   *
   * @memberof MallMedicineListPage
   */
  handleSelectedRow = (selectedRowKeys: number[]) => {
    this.setState({ selectedRowKeys });
    if (selectedRowKeys.length === 0 && this.tableComponent) {
      this.tableComponent.setState({ selectedRowKeys: [] });
    }
  };

  /**
   * 新增订单回调
   *
   * @memberof DoctorList
   */
  handleOrderVisible = (value: boolean, doRefresh = false) => {
    this.setState({
      visibleOrder: value,
    });
    if (doRefresh) {
      const { offset, limit } = this.state.query;
      this.handleSearch(offset, limit);
    }
  };

  handleAssignDocVisible = (value: boolean) => {
    this.setState({ visibleDocAssign: value });
    const { selectedRowKeys: selectedDocIds, dataSource: docsData } = this.state;

    const docInfos: IDoctorInfo[] = [];
    if (value) {
      for (const docId of selectedDocIds) {
        if (docsData) {
          for (const doc of docsData) {
            if (doc.id === docId) {
              docInfos.push(doc);
            }
          }
        } else {
          break;
        }
      }
      this.setState({ docInfos });
    }
  };

  /**
   * 编辑供应商
   *
   * @memberof DoctorList
   */
  handleChangeSupplier = async (data: IDoctorInfo, value = false) => {
    if (!data) {
      return;
    }
    const doctorInfoObj = await DoctorService.getAgentDoctorInfo(data.id);
    if (!!doctorInfoObj) {
      this.props.dispatch(selectDoctor(doctorInfoObj, DOCTOR_TYPE_LIST));
      this.setState({
        visibleSupplier: value,
      });
    }
  };

  /**
   * 新增订单
   *
   * @memberof DoctorList
   */
  handleAddOrder = async (data: IDoctorInfo, value = false, doRefresh = false) => {
    if (!data) {
      return;
    }
    const doctorInfoObj = await DoctorService.getAgentDoctorInfo(data.id);
    if (!!doctorInfoObj) {
      this.setState({
        visibleOrder: value,
      });
      const rechargeSeven = await DoctorService.getDoctorRechargeSevenCount({
        agent_uuid: doctorInfoObj.agent || '',
        organization_id: doctorInfoObj.organization_id,
      })
      doctorInfoObj.RechargeSevenCount = rechargeSeven ? rechargeSeven.agent_sevenly_left_count : 0;
      this.props.dispatch(selectDoctor(doctorInfoObj, DOCTOR_TYPE_LIST));
      if (doRefresh) {
        const { offset, limit } = this.state.query;
        this.handleSearch(offset, limit);
      }
    }
  };

  /**
   * @description 确定批量分配
   *
   */
  async batchAssignDocConfirmed(salespersonUuid: string | null, salespersonName: string | null) {
    this.setState({ visibleDocAssign: false });
    // 向后端post 分配给销售的医生
    const { selectedRowKeys: docIds } = this.state;
    try {
      this.setState({
        isLoading: true,
      });
      await DoctorService.distributeDocToSales(salespersonUuid, salespersonName, docIds);
      const { offset, limit } = this.state.query;
      this.handleSearch(offset, limit);
      message.success(I18N.get('common_save_success'));
      this.handleSelectedRow([]);
    } catch (error) {
      message.error(I18N.get('err_save_failure'));
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  }

  /**
   * 启用/禁用confirm
   *
   * @memberof DoctorList
   */
  handleDoctorHandle = (record: IDoctorInfo, isActive: boolean) => {
    if (!record) {
      return;
    }
    Modal.confirm({
      title: `${I18N.get('common_sure')}${isActive ? I18N.get('common_status_start') : I18N.get('common_status_stop')}${I18N.get('doc_current_user')}?`,
      content: ``,
      okText: `${isActive ? I18N.get('common_status_start') : I18N.get('common_status_stop')}`,
      cancelText: I18N.get('common_cancel'),
      onOk: () => this.handleDoctorActive(record, isActive),
      onCancel() { },
    });
  };

  /**
   * 启用/禁用
   *
   * @memberof DoctorList
   */
  handleDoctorActive = async (record: IDoctorInfo, isActive: boolean) => {
    if (!record) {
      return;
    }
    if (this.state.isLoading) {
      return;
    }
    try {
      this.setState({
        isLoading: true,
      });
      await DoctorService.updateAgentDoctorActive(record.uuid, isActive);
      const { offset, limit } = this.state.query;
      this.handleSearch(offset, limit);
      message.success(I18N.get('common_save_success'));
    } catch (error) {
      if (!!error.response) {
        if (error.response.status === 400 && error.response.data.err_code === 'has_sub_doctors') {
          message.error(I18N.get('err_save_has_sub_doctors'));
        } else {
          message.error(JSON.stringify(error.response.data));
        }
      } else {
        message.error(I18N.get('err_save_failure'));
      }
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  /**
   * 医生详情
   *
   * @memberof DoctorList
   */
  handleDoctorDetail = async (record: IDoctorInfo) => {
    if (!record) {
      return;
    }
    const doctorInfoObj = await DoctorService.getAgentDoctorInfo(record.id);
    if (!!doctorInfoObj) {
      this.props.dispatch(selectDoctor(doctorInfoObj, DOCTOR_TYPE_DETAIL));
      this.props.history.push({
        pathname: `${RouterConfig.Urls.userDoctor}/${record.id}`,
        state: {
          isNeedSetting: this.state.isNeedSetting,
          query: this.state.query,
        },
      });
    }
  };

  render() {
    const { isDomestic, isAgent } = this.props;
    const agentInfo = isAgent ? (this.props.userInfo as IAgentInfo) : undefined;

    let hasAssignAuth = false;
    if (!!agentInfo) {
      hasAssignAuth = agentInfo.customer_ownership;
    }
    const { isNeedSetting, allPharmacyList } = this.state;

    return (
      <div>
        {/* 医生搜索组件  */}
        <DoctorSearch
          isDomestic={isDomestic}
          isAgent={isAgent}
          hasAssignAuth={hasAssignAuth}
          isNeedSetting={isNeedSetting}
          allPharmacyList={allPharmacyList}
          cbSearch={this.doSearch}
          cbSelectedRow={(docIds: []) => this.handleSelectedRow(docIds)}
          query={!!this.props.location.state && !!this.props.location.state.query ? this.props.location.state.query : undefined}
        />
        {isDomestic && (
          <Row style={{ margin: '15px 0px', color: '#1FB8CD' }} gutter={0} type="flex" justify="space-between" align="middle">
            <div>{`${I18N.get('common_total_prefix')}${this.state.total || 0}${I18N.get('doc_total_suffix')}`}</div>
            <div>
              {/* 代理商是归属医生的权限才显示 */}
              {!!isAgent && !!hasAssignAuth && (
                <Button type="default" disabled={this.state.selectedRowKeys.length === 0} onClick={() => this.handleAssignDocVisible(true)}>
                  {I18N.get('doc_batch_assign')}
                </Button>
              )}
              {/* 代理商才显示批量编辑 */}
              {!!this.props.isAgent && (
                <Button style={{ marginLeft: 16 }} type="default" onClick={this.goToBatchModifyPage}>
                  {I18N.get('doc_batch_modify')}
                </Button>
              )}
              <Button style={{ marginLeft: 16 }} type="default" loading={this.state.isLoading} onClick={this.doctorExport}>
                <Icon type="download" />
                {I18N.get('common_export')}
              </Button>
            </div>
          </Row>
        )}

        {/* table */}
        <CommonTable
          rowKey={this.state.rowKey}
          showPagination={(this.state.total || 0) > 0}
          columns={this.state.columns}
          loadingTable={this.state.loadingTable}
          dataSource={this.state.dataSource}
          total={this.state.total}
          offset={this.state.query.offset}
          limit={this.state.query.limit}
          callback={this.handleSearch}
          cbSelectedRow={this.handleSelectedRow}
          ref={(node) => (this.tableComponent = node)}
        />
        <Modal
          title={`${I18N.get('common_edit')}${I18N.get('med_supplier')}`}
          width={900}
          bodyStyle={{ maxHeight: 'calc(100vh - 180px)', overflow: 'auto' }}
          destroyOnClose={true}
          visible={this.state.visibleSupplier}
          maskClosable={false}
          footer={null}
          onCancel={() => this.handleSupplierVisible(false)}
        >
          <SupplierEdit callback={() => this.handleSupplierVisible(false, true)} />
        </Modal>
        <Modal
          title={`${I18N.get('common_new')}${I18N.get('common_orders')}`}
          destroyOnClose={true}
          visible={this.state.visibleOrder}
          footer={null}
          onCancel={() => this.handleOrderVisible(false)}
        >
          <VASOrderAdd callback={() => this.handleOrderVisible(false, true)} />
        </Modal>

        <Modal
          title={`${I18N.get('doc_assign_sales')}`}
          destroyOnClose={true}
          visible={this.state.visibleDocAssign}
          footer={null}
          maskClosable={false}
          onCancel={() => this.handleAssignDocVisible(false)}
        >
          <div>
            <AssignSales
              docInfos={this.state.docInfos}
              onSubmit={(salespersonUuid: string | null, salespersonName: string | null) => this.batchAssignDocConfirmed(salespersonUuid, salespersonName)}
            />
          </div>
        </Modal>
      </div>
    );
  }
}

export default DoctorList;



// WEBPACK FOOTER //
// ./src/components/views/Doctor/DoctorList.tsx